import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, fehlertext } from "./api";
import { Anlagenzustand, Einrichtung } from "./anlage";
import { alsDatumUndZeit, heute } from "./datum";
import { Feld, Fehler, Karte, Knopf, Laedt, Seitenkopf } from "./ui";

/**
 * Angaben zur Firma, so wie sie nach aussen gehen: in Impressum und
 * Datenschutzerklaerung, in der Einladungsmail und im Hinweisgeberkanal.
 *
 * Diese Seite gibt es, weil die Werte sonst nur ueber eine Datenbankmigration
 * zu aendern waeren. Eine Telefonnummer ist kein Grund fuer eine Auslieferung,
 * und wer auf jemanden warten muss, um sie zu berichtigen, laesst sie stehen.
 *
 * Leere Felder sind erlaubt und bedeuten: dieser Weg wird nicht angeboten.
 * Eine fehlende Telefonnummer laesst die Zeile im Impressum weg, statt eine
 * leere zu drucken.
 *
 * Ansprechpartner und WhatsApp standen hier, solange das Eigentuemerportal
 * sie anzeigte. Mit dem Portal haben sie ihren einzigen Leser verloren, und
 * ein Feld, das niemand liest, wird trotzdem gepflegt und veraltet still.
 * Deshalb sind sie entfernt und nicht nur ausgeblendet.
 */

interface Firma {
  name: string;
  telefon: string;
  email: string;
  firmierung: string;
  nif: string;
  anschrift: string;
  register: string;
  datenschutzEmail: string;
}

const LEER: Firma = {
  name: "", telefon: "", email: "",
  firmierung: "", nif: "", anschrift: "", register: "", datenschutzEmail: ""
};

export function EinstellungenSeite({ darfAendern }: { darfAendern: boolean }) {
  const { t } = useTranslation();
  const [firma, setFirma] = useState<Firma | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    void api.get<Firma>("/firma")
      .then((f) => setFirma({ ...LEER, ...f }))
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [t]);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    if (!firma) return;
    setLaeuft(true);
    setFehler(null);
    setGespeichert(false);
    try {
      await api.patch<Firma>("/firma", firma);
      setGespeichert(true);
    } catch (err) {
      setFehler(fehlertext(err, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  function setze(feld: keyof Firma, wert: string) {
    setFirma((f) => (f ? { ...f, [feld]: wert } : f));
    setGespeichert(false);
  }

  if (!firma) return <Laedt />;

  return (
    <div className="max-w-xl">
      <Seitenkopf titel={t("einstellungen.titel")} hinweis={t("einstellungen.hinweis")} />

      {/*
        * Was fehlt und ob die Anlage gesund ist, steht ganz oben.
        *
        * Beides stand bis zum 04.09.2026 auf der Uebersicht. Hier ist die
        * richtige Stelle: wer einrichtet, ist hier, und wer wissen will, ob
        * die Sicherung noch laeuft, sucht sie hier. Beides verschwindet von
        * selbst, wenn es nichts zu melden gibt.
        */}
      {darfAendern && <Einrichtung />}
      {darfAendern && <Anlagenzustand />}

      <form onSubmit={speichern} className="mt-10">
      <Fehler text={fehler} />

      <Karte className="space-y-4">
        <Feld label={t("einstellungen.firma")}>
          <input className={EINGABE} value={firma.name} disabled={!darfAendern}
            onChange={(e) => setze("name", e.target.value)} />
        </Feld>
        <Feld label={t("einstellungen.telefon")}>
          <input className={EINGABE} value={firma.telefon} disabled={!darfAendern}
            onChange={(e) => setze("telefon", e.target.value)} />
        </Feld>
        <Feld label={t("einstellungen.email")} hinweis={t("einstellungen.emailHinweis")}>
          <input className={EINGABE} type="email" value={firma.email} disabled={!darfAendern}
            onChange={(e) => setze("email", e.target.value)} />
        </Feld>
      </Karte>

      {/*
        * Die Pflichtangaben aus Impressum und Datenschutzerklaerung.
        *
        * Sie stehen hier und nicht im Quelltext der Oberflaeche, weil ein
        * Rechtstext, den nur ein Entwickler berichtigen kann, nach dem
        * naechsten Umzug falsch stehen bleibt. Solange ein Feld leer ist,
        * fehlt die Zeile im Impressum, und das ist nach Art. 10 LSSI ein
        * Verstoss. Deshalb steht der Hinweis oben und nicht klein darunter.
        */}
      <Karte className="mt-4 space-y-4">
        <h2 className="font-bold">{t("einstellungen.rechtTitel")}</h2>
        <div className="rounded border-l-4 border-marke bg-grund/60 p-3 text-sm">
          {t("einstellungen.rechtWarnung")}
        </div>
        <Feld label={t("einstellungen.firmierung")} hinweis={t("einstellungen.firmierungHinweis")}>
          <input className={EINGABE} value={firma.firmierung} disabled={!darfAendern}
            onChange={(e) => setze("firmierung", e.target.value)} />
        </Feld>
        <Feld label={t("einstellungen.nif")}>
          <input className={EINGABE} value={firma.nif} disabled={!darfAendern}
            onChange={(e) => setze("nif", e.target.value)} />
        </Feld>
        <Feld label={t("einstellungen.anschrift")} hinweis={t("einstellungen.anschriftHinweis")}>
          <textarea className={EINGABE} rows={3} value={firma.anschrift} disabled={!darfAendern}
            onChange={(e) => setze("anschrift", e.target.value)} />
        </Feld>
        <Feld label={t("einstellungen.register")} hinweis={t("einstellungen.registerHinweis")}>
          <textarea className={EINGABE} rows={2} value={firma.register} disabled={!darfAendern}
            onChange={(e) => setze("register", e.target.value)} />
        </Feld>
        <Feld label={t("einstellungen.datenschutzEmail")} hinweis={t("einstellungen.datenschutzEmailHinweis")}>
          <input className={EINGABE} type="email" value={firma.datenschutzEmail} disabled={!darfAendern}
            onChange={(e) => setze("datenschutzEmail", e.target.value)} />
        </Feld>
      </Karte>

      {darfAendern && (
        <div className="mt-4 flex items-center gap-3">
          <Knopf type="submit" disabled={laeuft}>
            {laeuft ? t("aktion.speichert") : t("aktion.speichern")}
          </Knopf>
          {gespeichert && <span className="text-sm text-leise">{t("einstellungen.gespeichert")}</span>}
        </div>
      )}

      {darfAendern && <Betriebsschalter />}
      {darfAendern && <Musterberichte />}
      </form>

      {/*
        * Die Schluessel stehen ausserhalb des Formulars.
        *
        * Nicht aus Geschmack: ein Knopf innerhalb eines Formulars sendet es
        * ab, wenn niemand type="button" schreibt, und "Schluessel anlegen"
        * darf niemals versehentlich die Firmendaten speichern oder umgekehrt.
        */}
      {darfAendern && <Schnittstelle />}
    </div>
  );
}

const EINGABE = "w-full rounded border border-linie/40 px-3 py-2 outline-none focus:border-marke disabled:bg-grund/40";

/**
 * Musterberichte.
 *
 * Sie standen urspruenglich fuer die Vorschau des Eigentuemerportals. Mit
 * dessen Wegfall sind sie nicht ueberfluessig geworden, sondern haben zwei
 * Abnehmer bekommen: den Verlauf am Objekt, der ohne Berichte nichts zu zeigen
 * hat, und die Vorfuehrung beim Interessenten, die ohne Zahlen nach einer
 * leeren Anwendung aussieht.
 *
 * Ein einzelner Bericht von heute reicht dafuer nicht. Erst eine Reihe zeigt
 * den Verlauf des Wasserzaehlers und die Poolwerte.
 *
 * Der Knopf zum Entfernen steht gleichberechtigt daneben und nicht klein
 * darunter. Musterdaten, die man nur anlegen kann, sind ab dem ersten Tag eine
 * Altlast.
 */
function Musterberichte() {
  const { t } = useTranslation();
  const [kunden, setKunden] = useState<{ id: string; display_name: string }[]>([]);
  const [kundeId, setKundeId] = useState("");
  const [monat, setMonat] = useState(() => heute().slice(0, 7));
  const [anzahl, setAnzahl] = useState(8);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<string | null>(null);

  useEffect(() => {
    void api.get<{ customers: { id: string; display_name: string }[] }>("/customers?limit=200")
      .then((a) => setKunden(a.customers)).catch(() => setKunden([]));
  }, []);

  async function tun(was: () => Promise<string>) {
    setLaeuft(true); setFehler(null); setErgebnis(null);
    try {
      setErgebnis(await was());
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <>
      <h2 className="mt-10 text-lg font-bold">{t("muster.titel")}</h2>
      <p className="mt-1 text-sm text-leise">{t("muster.hinweis")}</p>
      <Fehler text={fehler} />
      {ergebnis && (
        <p className="mt-3 rounded border border-marke/40 bg-marke/10 px-3 py-2 text-sm">{ergebnis}</p>
      )}

      <Karte className="mt-3 space-y-4">
        <Feld label={t("muster.kunde")}>
          <select
            className={EINGABE}
            value={kundeId}
            onChange={(e) => setKundeId(e.target.value)}
          >
            <option value="">{t("muster.kundeWaehlen")}</option>
            {kunden.map((k) => (
              <option key={k.id} value={k.id}>{k.display_name}</option>
            ))}
          </select>
        </Feld>
        <div className="flex flex-wrap gap-4">
          <Feld label={t("muster.monat")}>
            <input className={EINGABE} type="month" value={monat}
              onChange={(e) => setMonat(e.target.value)} />
          </Feld>
          <Feld label={t("muster.anzahl")} hinweis={t("muster.anzahlHinweis")}>
            <input className={EINGABE} type="number" min={1} max={12} value={anzahl}
              onChange={(e) => setAnzahl(Math.max(1, Math.min(12, Number(e.target.value) || 1)))} />
          </Feld>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Knopf
            type="button"
            disabled={!kundeId || laeuft}
            onClick={() => void tun(async () => {
              const a = await api.post<{
                kunde: string; objekt: string; punkteJeBericht: number;
                angelegt: { nummer: number; tag: string }[];
              }>("/musterberichte", { kundeId, monat, anzahl });
              return t("muster.angelegt", {
                count: a.angelegt.length, kunde: a.kunde,
                punkte: a.punkteJeBericht,
                tage: a.angelegt.map((x) => x.tag).join(", ")
              });
            })}
          >
            {t("muster.anlegen")}
          </Knopf>
          <Knopf
            art="warnung"
            type="button"
            disabled={!kundeId || laeuft}
            onClick={() => void tun(async () => {
              const a = await api.del<{ entfernt: number[] }>(
                `/musterberichte?kundeId=${kundeId}`
              );
              return t("muster.entfernt", { count: a.entfernt.length });
            })}
          >
            {t("muster.entfernen")}
          </Knopf>
        </div>
      </Karte>
    </>
  );
}

interface Betrieb {
  ortung: boolean;
  /** Tag, an dem die unterschriebene Mitarbeiterinformation vorlag. Leer: liegt nicht vor. */
  ortungInfoAm: string;
  ortungTage: number;
  maxAbstandM: number;
  kiAn: boolean;
  kiLimitCent: number;
  kiVorschlaege: number;
  bueroKopie: string;
  urlaubEinheit: "natural" | "laboral";
  planBeginn: string;
  planEnde: string;
  planTeamAb: number;
  planPauseNach: number;
  planPause: number;
  fahrtTempo: number;
  fahrtUmweg: number;
  startOrt: string;
}

/**
 * Ob der Dienstplan mit gemessenen Strassenzeiten rechnet oder mit Luftlinie.
 *
 * Nur eine Zeile, aber eine wichtige: der Unterschied zwischen beidem ist in
 * der Oberflaeche sonst unsichtbar, und dann faellt niemandem auf, dass der
 * Messlauf seit einem halben Jahr nicht mehr durchgelaufen ist.
 */
interface Streckenlage {
  anzahl: number;
  gemessenAm: string | null;
  moeglich: number;
  eichung: { umwegfaktor: number; tempoKmh: number; paare: number } | null;
}

function Streckenstand({ onEichen }: {
  onEichen: (tempo: number, umweg: number) => void;
}) {
  const { t } = useTranslation();
  const [stand, setStand] = useState<Streckenlage | null>(null);

  useEffect(() => {
    void api.get<Streckenlage>("/strecken").then(setStand).catch(() => setStand(null));
  }, []);

  if (!stand) return null;
  return (
    <div className="space-y-2">
      <p className="text-sm text-leise">
        {stand.anzahl === 0
          ? t("betrieb.streckenKeine")
          : t("betrieb.streckenStand", {
              anzahl: stand.anzahl,
              moeglich: stand.moeglich,
              datum: stand.gemessenAm ?? ""
            })}
      </p>
      {/*
        * Die Eichung steht neben den beiden Feldern, die sie betrifft, und
        * traegt sich nicht von selbst ein. Zwei geratene Zahlen durch zwei
        * gerechnete zu ersetzen ist eine Entscheidung, und sie gehoert dem,
        * der sie verantwortet.
        */}
      {stand.eichung && (
        <p className="text-sm">
          {t("betrieb.eichung", {
            tempo: stand.eichung.tempoKmh,
            umweg: String(stand.eichung.umwegfaktor).replace(".", ","),
            paare: stand.eichung.paare
          })}{" "}
          <button
            type="button"
            className="min-h-11 font-semibold text-verweis underline"
            onClick={() => onEichen(stand.eichung!.tempoKmh, stand.eichung!.umwegfaktor)}
          >
            {t("betrieb.eichungUebernehmen")}
          </button>
        </p>
      )}
    </div>
  );
}

/**
 * Die Schalter des laufenden Betriebs.
 *
 * Bis hierher waren sie nur mit einer SQL-Anweisung auf dem Server zu aendern,
 * was in der Praxis heisst: gar nicht. Der wichtigste ist die Ortung, und
 * gerade der muss zu einem bestimmten Zeitpunkt umgelegt werden, naemlich
 * wenn die Information an die Beschaeftigten unterschrieben vorliegt.
 *
 * Nur fuer die Geschaeftsleitung. Ein Mitarbeiter muss die Telefonnummer der
 * Firma kennen, aber nicht das Monatsbudget fuer die Textvorschlaege.
 */
function Betriebsschalter() {
  const { t } = useTranslation();
  const [b, setB] = useState<Betrieb | null>(null);
  const [budget, setBudget] = useState<Budgetstand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    void api.get<Betrieb>("/betrieb").then(setB).catch(() => setB(null));
    void api.get<Budgetstand>("/ai/budget").then(setBudget).catch(() => setBudget(null));
  }, []);

  function setze<K extends keyof Betrieb>(feld: K, wert: Betrieb[K]) {
    setB((alt) => (alt ? { ...alt, [feld]: wert } : alt));
    setGespeichert(false);
  }

  async function speichern() {
    if (!b) return;
    setLaeuft(true);
    setFehler(null);
    try {
      setB(await api.patch<Betrieb>("/betrieb", b));
      setGespeichert(true);
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  if (!b) return null;

  return (
    <div className="mt-10">
      <h2 className="text-xl font-bold">{t("betrieb.titel")}</h2>
      <p className="mt-1 text-sm text-leise">{t("betrieb.hinweis")}</p>
      <Fehler text={fehler} />

      <Karte className="mt-3 space-y-4">
        {/* Der Warnkasten steht ueber dem Schalter und nicht darunter. Wer ihn
            erst nach dem Umlegen liest, hat ihn zu spaet gelesen. */}
        <div className="rounded border-l-4 border-marke bg-grund/60 p-3 text-sm">
          {t("betrieb.ortungWarnung")}
        </div>
        <label className="flex items-center gap-3 text-sm font-semibold">
          <input
            type="checkbox" checked={b.ortung}
            onChange={(e) => setze("ortung", e.target.checked)}
          />
          {t("betrieb.ortung")}
        </label>

        {/*
          Das Datum steht direkt unter dem Schalter, denn es gehoert zu ihm:
          ohne Datum nimmt der Server das Einschalten nicht an. Es ist auch die
          Angabe, nach der bei einer Pruefung als erstes gefragt wird, und
          zugleich das, was die Erinnerung auf der Uebersicht abhakt.
        */}
        <Feld label={t("betrieb.ortungInfoAm")} hinweis={t("betrieb.ortungInfoAmHinweis")}>
          <input
            className={EINGABE} type="date" max={heute()} value={b.ortungInfoAm}
            onChange={(e) => setze("ortungInfoAm", e.target.value)}
          />
        </Feld>

        <Feld label={t("betrieb.ortungTage")} hinweis={t("betrieb.ortungTageHinweis")}>
          <input
            className={EINGABE} type="number" min={1} max={1461} value={b.ortungTage}
            onChange={(e) => setze("ortungTage", Number(e.target.value) || b.ortungTage)}
          />
        </Feld>
        <Feld label={t("betrieb.maxAbstand")} hinweis={t("betrieb.maxAbstandHinweis")}>
          <input
            className={EINGABE} type="number" min={50} max={50000} step={50} value={b.maxAbstandM}
            onChange={(e) => setze("maxAbstandM", Number(e.target.value) || b.maxAbstandM)}
          />
        </Feld>
        {/* Beide Zaehlweisen sind in spanischen Verträgen verbreitet, und
            dieselben Ferien ergeben je nach Einheit ganz verschiedene Zahlen.
            Deshalb wird sie eingestellt und nicht geraten. */}
        <Feld label={t("betrieb.urlaubEinheit")} hinweis={t("betrieb.urlaubEinheitHinweis")}>
          <select
            className={EINGABE}
            value={b.urlaubEinheit}
            onChange={(e) => setze("urlaubEinheit", e.target.value as "natural" | "laboral")}
          >
            <option value="natural">{t("urlaub.einheit.natural")}</option>
            <option value="laboral">{t("urlaub.einheit.laboral")}</option>
          </select>
        </Feld>
      </Karte>

      {/*
        * Der Rahmen des Wochenvorschlags und die Annahmen hinter der Fahrzeit.
        * Ob fuenfundvierzig Stundenkilometer auf dieser Insel stimmen, zeigt
        * sich erst nach einem Sommer. Dafuer soll niemand den Quelltext
        * anfassen muessen.
        */}
      <Karte className="mt-4 space-y-4">
        <h3 className="font-bold">{t("betrieb.planTitel")}</h3>
        <p className="text-sm text-leise">{t("betrieb.planHinweis")}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Feld label={t("betrieb.planBeginn")}>
            <input
              className={EINGABE} type="time" value={b.planBeginn}
              onChange={(e) => setze("planBeginn", e.target.value || b.planBeginn)}
            />
          </Feld>
          <Feld label={t("betrieb.planEnde")}>
            <input
              className={EINGABE} type="time" value={b.planEnde}
              onChange={(e) => setze("planEnde", e.target.value || b.planEnde)}
            />
          </Feld>
        </div>
        <Feld label={t("betrieb.planTeamAb")} hinweis={t("betrieb.planTeamAbHinweis")}>
          <input
            className={EINGABE} type="number" min={30} max={960} step={30} value={b.planTeamAb}
            onChange={(e) => setze("planTeamAb", Number(e.target.value) || b.planTeamAb)}
          />
        </Feld>
        <div className="grid gap-4 sm:grid-cols-2">
          <Feld label={t("betrieb.planPauseNach")} hinweis={t("betrieb.planPauseNachHinweis")}>
            <input
              className={EINGABE} type="number" min={60} max={960} step={15} value={b.planPauseNach}
              onChange={(e) => setze("planPauseNach", Number(e.target.value) || b.planPauseNach)}
            />
          </Feld>
          <Feld label={t("betrieb.planPause")} hinweis={t("betrieb.planPauseHinweis")}>
            <input
              className={EINGABE} type="number" min={15} max={240} step={5} value={b.planPause}
              onChange={(e) => setze("planPause", Number(e.target.value) || b.planPause)}
            />
          </Feld>
        </div>
        <Feld label={t("betrieb.fahrtTempo")} hinweis={t("betrieb.fahrtTempoHinweis")}>
          <input
            className={EINGABE} type="number" min={5} max={130} value={b.fahrtTempo}
            onChange={(e) => setze("fahrtTempo", Number(e.target.value) || b.fahrtTempo)}
          />
        </Feld>
        <Feld label={t("betrieb.fahrtUmweg")} hinweis={t("betrieb.fahrtUmwegHinweis")}>
          <input
            className={EINGABE} type="number" min={1} max={3} step={0.05} value={b.fahrtUmweg}
            onChange={(e) => setze("fahrtUmweg", Number(e.target.value) || b.fahrtUmweg)}
          />
        </Feld>
        <Streckenstand
          onEichen={(tempo, umweg) => {
            setze("fahrtTempo", tempo);
            setze("fahrtUmweg", umweg);
          }}
        />
        <Feld label={t("betrieb.startOrt")} hinweis={t("betrieb.startOrtHinweis")}>
          <input
            className={EINGABE} type="text" maxLength={120} value={b.startOrt}
            onChange={(e) => setze("startOrt", e.target.value)}
          />
        </Feld>
      </Karte>

      <Karte className="mt-4 space-y-4">
        <label className="flex items-center gap-3 text-sm font-semibold">
          <input
            type="checkbox" checked={b.kiAn}
            onChange={(e) => setze("kiAn", e.target.checked)}
          />
          {t("betrieb.kiAn")}
        </label>
        {/* Der Verbrauch steht ueber der Grenze und nicht daneben. Wer die
            Grenze anhebt, soll vorher gesehen haben, wofuer. */}
        {budget && (
          <Verbrauch verbrauchtCent={budget.verbrauchtCent} grenzeCent={budget.grenzeCent} />
        )}
        <Feld label={t("betrieb.kiLimit")} hinweis={t("betrieb.kiLimitHinweis")}>
          <input
            className={EINGABE} type="number" min={0} step={5}
            value={b.kiLimitCent / 100}
            onChange={(e) => setze("kiLimitCent", Math.round((Number(e.target.value) || 0) * 100))}
          />
        </Feld>
        <Feld label={t("betrieb.kiVorschlaege")} hinweis={t("betrieb.kiVorschlaegeHinweis")}>
          <input
            className={EINGABE} type="number" min={0} max={10} value={b.kiVorschlaege}
            onChange={(e) => setze("kiVorschlaege", Number(e.target.value) || 0)}
          />
        </Feld>
        <Feld label={t("betrieb.bueroKopie")} hinweis={t("betrieb.bueroKopieHinweis")}>
          <input
            className={EINGABE} type="email" value={b.bueroKopie}
            onChange={(e) => setze("bueroKopie", e.target.value)}
          />
        </Feld>
      </Karte>

      <div className="mt-4 flex items-center gap-3">
        <Knopf disabled={laeuft} onClick={() => void speichern()}>
          {laeuft ? t("aktion.speichert") : t("aktion.speichern")}
        </Knopf>
        {gespeichert && <span className="text-sm text-leise">{t("einstellungen.gespeichert")}</span>}
      </div>
    </div>
  );
}

interface Budgetstand {
  grenzeCent: number;
  verbrauchtCent: number;
  aktiv: boolean;
  eingerichtet: boolean;
  modell: string;
}

/**
 * Was in diesem Monat schon ausgegeben wurde.
 *
 * Ein Balken und zwei Zahlen. Der Betrag ist klein, aber er ist der einzige
 * laufende Posten, der mit der Nutzung waechst, und ohne Anzeige merkt man
 * das erst an der Rechnung.
 */
function Verbrauch({ verbrauchtCent, grenzeCent }: { verbrauchtCent: number; grenzeCent: number }) {
  const { t } = useTranslation();
  const anteil = grenzeCent > 0 ? Math.min(100, (verbrauchtCent / grenzeCent) * 100) : 0;
  const euro = (cent: number) => `${(cent / 100).toFixed(2).replace(".", ",")} €`;

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-2 text-sm">
        <span className="font-semibold">{t("betrieb.verbrauch")}</span>
        <span>{t("betrieb.verbrauchStand", { verbraucht: euro(verbrauchtCent), grenze: euro(grenzeCent) })}</span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded bg-grund">
        <div
          className={`h-full ${anteil >= 100 ? "bg-red-600" : "bg-marke"}`}
          style={{ width: `${anteil}%` }}
        />
      </div>
    </div>
  );
}

interface Schluesselzeile {
  id: string;
  name: string;
  prefix: string;
  angelegt: string;
  zuletztBenutzt: string | null;
  widerrufen: string | null;
}

/**
 * Die Schluessel fuer die offene Schnittstelle.
 *
 * Hier haengt der Betrieb seine Buchhaltung, seine Gestoria oder sein eigenes
 * Rechnungsprogramm an. Ein Schluessel je Abnehmer, damit man einen einzelnen
 * widerrufen kann, ohne die anderen abzuschalten. Genau darum steht der Name
 * daneben und ist Pflicht: "Gestoria Palma" laesst sich in zwei Jahren noch
 * widerrufen, "Schluessel 3" nicht.
 *
 * **Der Klartext steht genau einmal da.** Danach ist er weg, denn gespeichert
 * ist nur sein Hash. Der Kasten bleibt deshalb offen, bis er weggeklickt wird,
 * und sagt ausdruecklich, dass er nicht wiederkommt. Wer ihn verliert, legt
 * einen neuen an; das kostet eine Minute, ein wiederanzeigbares Geheimnis
 * kostet im Zweifel den Datenbestand.
 *
 * **Widerrufene bleiben in der Liste**, ausgegraut und mit Datum. Nach einem
 * Vorfall ist die Frage nicht, welche Zugaenge es gibt, sondern welche es gab.
 */
function Schnittstelle() {
  const { t } = useTranslation();
  const [liste, setListe] = useState<Schluesselzeile[] | null>(null);
  const [name, setName] = useState("");
  const [neuer, setNeuer] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function laden() {
    try {
      const a = await api.get<{ schluessel: Schluesselzeile[] }>("/schnittstelle/schluessel");
      setListe(a.schluessel);
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.laden")));
      setListe([]);
    }
  }

  useEffect(() => { void laden(); }, []);

  async function anlegen() {
    setLaeuft(true);
    setFehler(null);
    try {
      const a = await api.post<{ schluessel: string }>("/schnittstelle/schluessel", { name });
      setNeuer(a.schluessel);
      setName("");
      await laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  async function widerrufen(z: Schluesselzeile) {
    setFehler(null);
    try {
      await api.del(`/schnittstelle/schluessel/${z.id}`);
      await laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <div className="mt-10">
      <h2 className="text-xl font-bold">{t("schnittstelle.titel")}</h2>
      <p className="mt-1 text-sm text-leise">{t("schnittstelle.hinweis")}</p>
      <Fehler text={fehler} />

      <Karte className="mt-3 space-y-4">
        {/*
          * Der Klartext steht ueber der Liste und nicht darin. In der Liste
          * wuerde er beim naechsten Laden verschwinden, und das waere genau
          * der Moment, in dem ihn noch niemand kopiert hat.
          */}
        {neuer && (
          <div className="rounded border-l-4 border-marke bg-grund/60 p-3">
            <p className="text-sm font-semibold">{t("schnittstelle.einmalig")}</p>
            <code className="mt-2 block break-all rounded bg-white p-2 text-xs">{neuer}</code>
            <div className="mt-3">
              <Knopf type="button" art="neben" onClick={() => setNeuer(null)}>
                {t("schnittstelle.verstanden")}
              </Knopf>
            </div>
          </div>
        )}

        <Feld label={t("schnittstelle.name")} hinweis={t("schnittstelle.nameHinweis")}>
          <input
            className={EINGABE} value={name} maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />
        </Feld>
        <Knopf
          type="button" disabled={laeuft || name.trim().length < 2}
          onClick={() => void anlegen()}
        >
          {t("schnittstelle.anlegen")}
        </Knopf>

        {liste === null ? <Laedt /> : liste.length === 0 ? (
          <p className="text-sm text-leise">{t("schnittstelle.leer")}</p>
        ) : (
          <ul className="divide-y divide-linie/40 border-t border-linie/40">
            {liste.map((z) => (
              <li key={z.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className={`grow ${z.widerrufen ? "opacity-50" : ""}`}>
                  <p className="text-sm font-semibold">{z.name}</p>
                  <p className="text-xs text-leise">
                    <code>jor_{z.prefix}…</code>
                    {" · "}{t("schnittstelle.angelegt")}: {alsDatumUndZeit(z.angelegt)}
                    {" · "}
                    {z.zuletztBenutzt
                      ? `${t("schnittstelle.benutzt")}: ${alsDatumUndZeit(z.zuletztBenutzt)}`
                      : t("schnittstelle.nieBenutzt")}
                    {z.widerrufen && ` · ${t("schnittstelle.widerrufenAm")}: ${alsDatumUndZeit(z.widerrufen)}`}
                  </p>
                </div>
                {!z.widerrufen && (
                  <Knopf type="button" art="warnung" onClick={() => void widerrufen(z)}>
                    {t("schnittstelle.widerrufen")}
                  </Knopf>
                )}
              </li>
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
}
